/**
 * PLAN pure helpers: the light bands for the sun-planner track, alert-rule templates → specs and
 * their one-line summaries, camera FOV text, invite status.
 */
import {
  cameraFov,
  formatDuration,
  lightingForView,
  sunPosition,
  type LatLon,
  type Lighting,
  sunEventLabel,
  targetLabel,
  type AircraftSelector,
  type AlertRule,
  type AlertRuleSpec,
  type AlertSeverity,
  type Camera,
  type EpochMs,
  type LightPhase,
  type MissionInvite,
  type MissionRecord,
  type SunEventName,
  type SunTimes,
  type TargetRef,
} from '@apos/shared';

// ───────────── sun planner bands ─────────────

export interface LightBand {
  from: EpochMs;
  to: EpochMs;
  phase: LightPhase;
}

/**
 * Contiguous light-phase segments covering [dayStart, dayEnd] from shared `sunTimes`:
 * night → twilights → blue hour → golden hour → day → golden → blue → twilights → night.
 * Missing crossings (polar day/night) simply drop their boundary.
 */
export function lightBands(times: SunTimes, dayStart: EpochMs, dayEnd: EpochMs): LightBand[] {
  const edges: Array<{ t: EpochMs | null | undefined; phase: LightPhase }> = [
    { t: times.astronomicalDawn, phase: 'astronomical_twilight' },
    { t: times.nauticalDawn, phase: 'nautical_twilight' },
    { t: times.blueHourMorning?.start ?? times.civilDawn, phase: 'blue_hour' },
    { t: times.blueHourMorning?.end, phase: 'golden_hour' },
    { t: times.goldenHourMorningEnd, phase: 'day' },
    { t: times.goldenHourEveningStart, phase: 'golden_hour' },
    { t: times.blueHourEvening?.start, phase: 'blue_hour' },
    { t: times.civilDusk, phase: 'nautical_twilight' },
    { t: times.nauticalDusk, phase: 'astronomical_twilight' },
    { t: times.astronomicalDusk, phase: 'night' },
  ];
  const pts = edges.filter((e): e is { t: EpochMs; phase: LightPhase } => e.t != null && Number.isFinite(e.t)).sort((a, b) => a.t - b.t);
  const out: LightBand[] = [];
  let cur: LightPhase = pts.length ? phaseBefore(pts[0]!.phase) : times.maxElevationDeg > 6 ? 'day' : 'night';
  let from = dayStart;
  for (const p of pts) {
    const t = Math.min(dayEnd, Math.max(dayStart, p.t));
    if (t > from) out.push({ from, to: t, phase: cur });
    from = Math.max(from, t);
    cur = p.phase;
  }
  if (dayEnd > from) out.push({ from, to: dayEnd, phase: cur });
  // merge neighbours with the same phase
  return out.reduce<LightBand[]>((acc, b) => {
    const last = acc[acc.length - 1];
    if (last && last.phase === b.phase && last.to === b.from) last.to = b.to;
    else acc.push({ ...b });
    return acc;
  }, []);
}

function phaseBefore(first: LightPhase): LightPhase {
  // the phase in force before the first crossing of the day
  return first === 'astronomical_twilight' ? 'night' : first === 'day' ? 'golden_hour' : first === 'golden_hour' ? 'day' : 'night';
}

export interface LightingSegment {
  from: EpochMs;
  to: EpochMs;
  lighting: Lighting;
  flare: boolean;
}

/**
 * How a camera looking along `headingDeg` from `pos` is lit through the day (shared
 * `lightingForView` sampled every `stepMs`), merged into segments — the per-position band on the
 * sun planner ("when is this position back-lit?").
 */
export function lightingSegments(pos: LatLon, headingDeg: number, dayStart: EpochMs, dayEnd: EpochMs, stepMs = 10 * 60_000): LightingSegment[] {
  const out: LightingSegment[] = [];
  for (let t = dayStart; t < dayEnd; t += stepMs) {
    const l = lightingForView(headingDeg, sunPosition(t + stepMs / 2, pos.lat, pos.lon));
    const to = Math.min(dayEnd, t + stepMs);
    const last = out[out.length - 1];
    if (last && last.lighting === l.lighting && last.flare === l.flareRisk) last.to = to;
    else out.push({ from: t, to, lighting: l.lighting, flare: l.flareRisk });
  }
  return out;
}

// ───────────── alert rule templates ─────────────

export type RuleTemplate = 'proximity' | 'eta' | 'sun' | 'weather_change' | 'stale_data' | 'separation';

export const RULE_TEMPLATES: Array<{ id: RuleTemplate; label: string; hint: string }> = [
  { id: 'proximity', label: 'Proximity', hint: 'Aircraft within N NM of a person, camera, site or airport' },
  { id: 'eta', label: 'ETA', hint: 'Aircraft estimated N minutes from a place' },
  { id: 'sun', label: 'Sun / golden hour', hint: 'Lead time before sunset, golden hour, sunrise…' },
  { id: 'weather_change', label: 'Weather change', hint: 'Flight category, wind, gusts, precipitation' },
  { id: 'stale_data', label: 'Stale data', hint: 'No position from an aircraft for N seconds' },
  { id: 'separation', label: 'A2A separation', hint: 'Two aircraft further apart / closer than N NM' },
];

export interface RuleDraft {
  template: RuleTemplate;
  aircraft: AircraftSelector;
  aircraftB: string;
  target: TargetRef | null;
  nm: number;
  minutes: number;
  seconds: number;
  sunEvent: SunEventName;
  stationId: string | null;
  windIncreaseKt: number;
  gustKt: number;
  categoryChange: boolean;
  precipStart: boolean;
  direction: 'inside' | 'outside';
  severity: AlertSeverity;
}

export function defaultDraft(template: RuleTemplate, record: Pick<MissionRecord, 'aircraft'>): RuleDraft {
  const subject = record.aircraft.find((a) => a.role === 'subject') ?? record.aircraft[0];
  const other = record.aircraft.find((a) => a.id !== subject?.id);
  return {
    template,
    aircraft: subject?.id ?? 'any',
    aircraftB: other?.id ?? '',
    target: null,
    nm: template === 'separation' ? 1 : 2,
    minutes: 2,
    seconds: 45,
    sunEvent: 'sunset',
    stationId: null,
    windIncreaseKt: 10,
    gustKt: 25,
    categoryChange: true,
    precipStart: true,
    direction: 'outside',
    severity: template === 'stale_data' ? 'warning' : template === 'sun' ? 'info' : 'advisory',
  };
}

/** Build a rule (name + spec) from a template draft; returns an error string when incomplete. */
export function ruleFromDraft(
  d: RuleDraft,
  record: Pick<MissionRecord, 'members' | 'aircraft' | 'cameras' | 'locations' | 'airports'>,
): Omit<AlertRule, 'id' | 'missionId' | 'createdAt'> | string {
  const acName = (sel: AircraftSelector) => (sel === 'any' ? 'Any aircraft' : (record.aircraft.find((a) => a.id === sel)?.label ?? 'Aircraft'));
  const pos = (n: number, what: string) => (Number.isFinite(n) && n > 0 ? null : `${what} must be greater than 0`);
  let spec: AlertRuleSpec;
  let name: string;
  let cooldownSec = 600;
  switch (d.template) {
    case 'proximity': {
      if (!d.target) return 'Choose what the aircraft approaches';
      const e = pos(d.nm, 'Distance');
      if (e) return e;
      spec = { type: 'proximity', aircraft: d.aircraft, target: d.target, thresholdNm: d.nm };
      name = `${acName(d.aircraft)} within ${d.nm} NM of ${targetLabel(record, d.target)}`;
      break;
    }
    case 'eta': {
      if (!d.target) return 'Choose the place';
      const e = pos(d.minutes, 'Lead time');
      if (e) return e;
      spec = { type: 'eta', aircraft: d.aircraft, target: d.target, thresholdSec: Math.round(d.minutes * 60) };
      name = `${acName(d.aircraft)} ${d.minutes} min from ${targetLabel(record, d.target)}`;
      break;
    }
    case 'sun': {
      if (!(d.minutes >= 0)) return 'Lead time must be 0 or more';
      spec = { type: 'sun', event: d.sunEvent, leadMin: d.minutes };
      name = d.minutes > 0 ? `${sunEventLabel(d.sunEvent)} in ${d.minutes} minutes` : sunEventLabel(d.sunEvent);
      cooldownSec = 3600;
      break;
    }
    case 'weather_change':
      spec = {
        type: 'weather_change',
        stationId: d.stationId,
        windIncreaseKt: d.windIncreaseKt,
        gustKt: d.gustKt,
        onFlightCategoryChange: d.categoryChange,
        precipStart: d.precipStart,
      };
      name = `Weather change${d.stationId ? ` at ${d.stationId}` : ''}`;
      break;
    case 'stale_data': {
      const e = pos(d.seconds, 'Threshold');
      if (e) return e;
      spec = { type: 'stale_data', aircraft: d.aircraft, thresholdSec: Math.round(d.seconds) };
      name = `${acName(d.aircraft)} data stale`;
      cooldownSec = 300;
      break;
    }
    case 'separation': {
      if (d.aircraft === 'any' || !d.aircraftB) return 'Choose two aircraft';
      if (d.aircraft === d.aircraftB) return 'Choose two different aircraft';
      const e = pos(d.nm, 'Distance');
      if (e) return e;
      spec = { type: 'separation', aircraftA: d.aircraft, aircraftB: d.aircraftB, thresholdNm: d.nm, direction: d.direction };
      name = `${acName(d.aircraftB)} ${d.direction === 'outside' ? 'more than' : 'within'} ${d.nm} NM of ${acName(d.aircraft)}`;
      cooldownSec = 300;
      break;
    }
  }
  return { name, enabled: true, severity: d.severity, cooldownSec, spec };
}

/** One-line description of a rule's condition for lists. */
export function ruleSummary(spec: AlertRuleSpec, record: Pick<MissionRecord, 'members' | 'aircraft' | 'cameras' | 'locations' | 'airports'>): string {
  const ac = (sel: AircraftSelector) => (sel === 'any' ? 'any aircraft' : targetLabel(record, { kind: 'aircraft', id: sel }));
  switch (spec.type) {
    case 'proximity':
      return `${ac(spec.aircraft)} ≤ ${spec.thresholdNm} NM of ${targetLabel(record, spec.target)}`;
    case 'eta':
      return `${ac(spec.aircraft)} ETA ≤ ${formatDuration(spec.thresholdSec)} to ${targetLabel(record, spec.target)}`;
    case 'sun':
      return `${spec.leadMin} min before ${sunEventLabel(spec.event).toLowerCase()}`;
    case 'weather_change': {
      const parts = [
        spec.onFlightCategoryChange ? 'category change' : null,
        spec.windIncreaseKt ? `wind +${spec.windIncreaseKt} kt` : null,
        spec.gustKt ? `gusts ≥ ${spec.gustKt} kt` : null,
        spec.precipStart ? 'precip starts' : null,
      ].filter(Boolean);
      return `${spec.stationId ?? 'All stations'}: ${parts.join(', ') || 'any change'}`;
    }
    case 'stale_data':
      return `${ac(spec.aircraft)} no data for ${spec.thresholdSec} s`;
    case 'separation':
      return `${ac(spec.aircraftA)} ↔ ${ac(spec.aircraftB)} ${spec.direction === 'outside' ? '>' : '<'} ${spec.thresholdNm} NM`;
    default:
      // rule kinds added by other lanes after this list was written
      return (spec as { type: string }).type.replace(/_/g, ' ');
  }
}

// ───────────── cameras / invites ─────────────

/** "15.9° H × 10.9° V" or null. */
export function fovText(c: Pick<Camera, 'sensorWidthMm' | 'sensorHeightMm' | 'focalLengthMm'>): string | null {
  const f = cameraFov(c);
  return f ? `${f.hfovDeg.toFixed(1)}° H × ${f.vfovDeg.toFixed(1)}° V` : null;
}

export type InviteStatus = 'active' | 'expired' | 'used_up';

export function inviteStatus(inv: Pick<MissionInvite, 'expiresAt' | 'maxUses' | 'uses'>, now: EpochMs): InviteStatus {
  if (inv.expiresAt <= now) return 'expired';
  if (inv.maxUses !== null && inv.uses >= inv.maxUses) return 'used_up';
  return 'active';
}

/** Default label for the next camera ("CAM A", "CAM B", … "CAM AA"). */
export function nextCameraLabel(existing: readonly string[]): string {
  const taken = new Set(existing.map((s) => s.trim().toUpperCase()));
  for (let i = 0; i < 702; i++) {
    const a = i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26));
    if (!taken.has(`CAM ${a}`)) return `CAM ${a}`;
  }
  return `CAM ${existing.length + 1}`;
}

/** Default name for a new place of a kind ("CAM SITE", "CAM SITE 2", …). */
export function nextPlaceName(base: string, existing: readonly string[]): string {
  const taken = new Set(existing.map((s) => s.trim().toUpperCase()));
  if (!taken.has(base.toUpperCase())) return base;
  for (let i = 2; i < 1000; i++) if (!taken.has(`${base} ${i}`.toUpperCase())) return `${base} ${i}`;
  return base;
}
